import React, { useMemo } from "react";
import About from "../components/About";
import CompanyInformation from "../components/CompanyInformation";
import CompanyVerification from "./CompanyVerification";
import BackgroundVerification from "./BackgroundVerification";
import SkillSet from "./SkillSet";
import Equipments from "./Equipments";
import Language from "./Language";
import Employment from "./Employment";
import Education from "./Education";
import Resume from "./Resume";
import LicenseCertification from "./LicenseCertification";
import Rating from "./Rating";
import Reliability from "./Reliability";
import JobActivity from "./JobActivity";
import WorkSummary from "./WorkSummary";
import TalentPools from "./TalentPools";
import { fetchQualificationTypes } from "../../../services/api/qualification";
import { useQuery } from "@tanstack/react-query";
import { UserType } from "../../../utils/enum";
import useAuth from "../../../hooks/useAuth";
import { fetchAllUserLanguages } from "../../../services/api/language";
import UserInformation from "../components/UserInformation";

const Profile = ({ user, refetchMe, canAccessProfile, afterCompanyInfo = null }) => {
  const { authRole, auth } = useAuth();
  const isSelfView = auth?.user?.id === user?.id;
  const {
    isLoading: isQualificationsTypesLoading,
    error: isQualificationsTypesError,
    data: qualificationsTypesData,
  } = useQuery({
    queryKey: ["qualificationsTypes"],
    queryFn: fetchQualificationTypes,
    enabled: !!user && canAccessProfile,
  });
  const {
    isPending: isLanguagesLoading,
    error: isLanguagesError,
    data: languagesData,
    refetch: refetchAllUserLanguages,
  } = useQuery({
    queryKey: ["languages", user?.id],
    queryFn: () => fetchAllUserLanguages({ userId: user?.id }),
    enabled: !!user?.id && canAccessProfile,
  });
  const qualificationTypeMap = useMemo(() => {
    if (!qualificationsTypesData?.data?.data) return {};
    return qualificationsTypesData?.data?.data?.reduce((acc, item) => {
      acc[item.name.toLowerCase()] = item.id;
      return acc;
    }, {});
  }, [qualificationsTypesData]);

  const isFreelanceCompanyOwner = user?.role?.role === UserType.FREELANCER && user?.isCompanyOwner;

  // A freelance member viewing their own company owner's profile should not see the User Info card.
  const isMemberViewingOwner =
    authRole === UserType.FREELANCER && !isSelfView && isFreelanceCompanyOwner;

  // A customer's job is done by the vendor's tech, so the vendor owner is never
  // someone they deal with — their contact details aren't the customer's to read.
  const isCustomerViewingOwner = authRole === UserType.CUSTOMER && isFreelanceCompanyOwner;

  // A client reads a vendor's company here; an admin opens it on its own tab, and an
  // engineer's company is read under their personal information.
  const showsOwnerCompany = isFreelanceCompanyOwner && authRole === UserType.CLIENT;

  return (
    <div className="grid grid-cols-1">
      <div>
        {showsOwnerCompany && (
          <>
            <CompanyInformation company={user?.company} />
            <CompanyVerification company={user?.company} />
          </>
        )}
        {afterCompanyInfo}
        {isFreelanceCompanyOwner && !isMemberViewingOwner && !isCustomerViewingOwner && (
          <UserInformation
            refetchMe={refetchMe}
            canEdit={authRole === UserType.ADMIN}
            userInfo={{
              userId: user?.id,
              firstName: user?.firstName,
              lastName: user?.lastName,
              name: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
              email: user?.email,
              phoneNumber: user?.phoneNumber,
              createdAt: user?.createdAt,
              lastActive: user?.lastActive,
            }}
          />
        )}
        <About description={user?.description} />
        {user?.role?.role === UserType.FREELANCER && (
          <>
            {(authRole === UserType.CLIENT || authRole === UserType.ADMIN) && <TalentPools />}
            <Rating />
            <Reliability />
            <JobActivity />
            <WorkSummary />
          </>
        )}

        {user?.role?.role === UserType.FREELANCER && !isFreelanceCompanyOwner && (
          <>
            <SkillSet skills={user?.userSkills} userId={user?.id} />
            <Equipments equipmentId={qualificationTypeMap["equipment"]} />
            <BackgroundVerification backgroundCheckId={qualificationTypeMap["background check"]} />
            <LicenseCertification
              licencesId={qualificationTypeMap["licences"]}
              certificationId={qualificationTypeMap["certification"]}
            />
            <Education />
            {authRole !== UserType.CUSTOMER && (
              <Resume resume={user?.resume} refetchMe={refetchMe} userId={user?.id} />
            )}
            <Employment />
            <Language
              languages={languagesData?.data}
              userId={user?.id}
              refetchAllUserLanguages={refetchAllUserLanguages}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Profile;
